// ============================================================
// Ödeme Sözü Takip Durumları - Tanımlar ve Renkler
// ============================================================

export interface FollowUpStatusDef {
  key: string;
  label: string;
  color: string;       // tailwind text class
  bg: string;          // tailwind bg class
  badgeClass: string;  // full badge class
  icon: string;        // remix icon
}

export const FOLLOW_UP_STATUSES: FollowUpStatusDef[] = [
  { key: 'Bekliyor',         label: 'Bekliyor',          color: 'text-foreground-500',  bg: 'bg-foreground-100',  badgeClass: 'badge-neutral',        icon: 'ri-time-line' },
  { key: 'Aranacak',         label: 'Aranacak',          color: 'text-sky-600',         bg: 'bg-sky-50',          badgeClass: 'badge-info',           icon: 'ri-phone-line' },
  { key: 'Arandı',           label: 'Arandı',            color: 'text-indigo-600',      bg: 'bg-indigo-50',       badgeClass: 'badge-info',           icon: 'ri-phone-fill' },
  { key: 'Tekrar Aranacak',  label: 'Tekrar Aranacak',   color: 'text-orange-600',      bg: 'bg-orange-50',       badgeClass: 'badge-warning',        icon: 'ri-phone-find-line' },
  { key: 'Ulaşılamadı',      label: 'Ulaşılamayan',      color: 'text-red-600',         bg: 'bg-red-50',          badgeClass: 'badge-danger',         icon: 'ri-phone-lock-line' },
  { key: 'Akşam Ödeyecek',   label: 'Akşam Ödeyecek',    color: 'text-purple-600',      bg: 'bg-purple-50',       badgeClass: 'badge-accent',         icon: 'ri-moon-line' },
  { key: 'Yarın Ödeyecek',   label: 'Yarın Ödeyecek',    color: 'text-cyan-600',        bg: 'bg-cyan-50',         badgeClass: 'badge-info',           icon: 'ri-sun-line' },
  { key: 'Ertelendi',        label: 'Ertelendi',         color: 'text-amber-600',       bg: 'bg-amber-50',        badgeClass: 'badge-warning',        icon: 'ri-calendar-schedule-line' },
  { key: 'Kısmi Ödeme Yaptı',label: 'Kısmi Ödeme Yapıldı',color: 'text-emerald-600',     bg: 'bg-emerald-50',      badgeClass: 'badge-success',        icon: 'ri-bank-card-line' },
  { key: 'Ödeme Yaptı',      label: 'Söz Tutuldu',       color: 'text-green-700',       bg: 'bg-green-100',       badgeClass: 'badge-success',        icon: 'ri-check-double-line' },
  { key: 'Sözünü Tutmadı',   label: 'Sözünü Tutmayan',   color: 'text-red-700',         bg: 'bg-red-100',         badgeClass: 'badge-danger',         icon: 'ri-user-unfollow-line' },
  { key: 'İptal Edildi',     label: 'İptal Edildi',      color: 'text-foreground-400',  bg: 'bg-foreground-200',  badgeClass: 'badge-muted',          icon: 'ri-close-circle-line' },
];

const STATUS_MAP = new Map<string, FollowUpStatusDef>();
FOLLOW_UP_STATUSES.forEach(s => STATUS_MAP.set(s.key, s));

export function getFollowUpStatusDef(key: string): FollowUpStatusDef | undefined {
  return STATUS_MAP.get(key);
}

export function getFollowUpStatusLabel(key: string): string {
  return STATUS_MAP.get(key)?.label || key;
}

export function getFollowUpStatusColor(key: string): string {
  return STATUS_MAP.get(key)?.color || 'text-foreground-500';
}

export function getFollowUpStatusBg(key: string): string {
  return STATUS_MAP.get(key)?.bg || 'bg-foreground-100';
}

export function getFollowUpStatusBadgeClass(key: string): string {
  return STATUS_MAP.get(key)?.badgeClass || 'badge-neutral';
}

export function getFollowUpStatusIcon(key: string): string {
  return STATUS_MAP.get(key)?.icon || 'ri-question-line';
}

// Durumlar: Takip tarihi/not zorunlu olanlar
export const STATUSES_REQUIRING_FOLLOW_UP = ['Tekrar Aranacak', 'Akşam Ödeyecek', 'Yarın Ödeyecek', 'Ertelendi'];

// Filtreleme için durum grupları
export const STATUS_FILTER_GROUPS: { key: string; label: string; icon: string; statuses: string[] }[] = [
  { key: 'all',            label: 'Tümü',              icon: 'ri-list-check-2',      statuses: [] },
  { key: 'today_call',     label: 'Bugün Aranacaklar', icon: 'ri-phone-line',       statuses: ['Aranacak'] },
  { key: 'call_now',       label: 'Şimdi Aranacaklar', icon: 'ri-phone-fill',       statuses: ['Aranacak'] },
  { key: 'recall',         label: 'Tekrar Aranacaklar',icon: 'ri-phone-find-line',  statuses: ['Tekrar Aranacak'] },
  { key: 'evening',        label: 'Akşam Ödeyecekler', icon: 'ri-moon-line',        statuses: ['Akşam Ödeyecek'] },
  { key: 'tomorrow',       label: 'Yarın Ödeyecekler', icon: 'ri-sun-line',         statuses: ['Yarın Ödeyecek'] },
  { key: 'unreachable',    label: 'Ulaşılamayanlar',   icon: 'ri-phone-lock-line',  statuses: ['Ulaşılamadı'] },
  { key: 'defaulted',      label: 'Sözünü Tutmayanlar',icon: 'ri-user-unfollow-line', statuses: ['Sözünü Tutmadı'] },
  { key: 'paid',           label: 'Ödeme Yapanlar',    icon: 'ri-check-double-line', statuses: ['Ödeme Yaptı', 'Kısmi Ödeme Yaptı'] },
  { key: 'postponed',      label: 'Ertelenenler',      icon: 'ri-calendar-schedule-line', statuses: ['Ertelendi'] },
  { key: 'pending',        label: 'Bekleyenler',       icon: 'ri-time-line',         statuses: ['Bekliyor'] },
  { key: 'called',         label: 'Arananlar',         icon: 'ri-phone-fill',        statuses: ['Arandı'] },
  { key: 'cancelled',      label: 'İptal Edilenler',   icon: 'ri-close-circle-line', statuses: ['İptal Edildi'] },
];